<template>
  <fieldset class="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-alt-dark-border">
    <legend class="px-1 text-sm font-medium text-space-indigo dark:text-altoneo-50">Périodicité</legend>
    <label :for="`${uid}-frequence`" class="block text-xs text-slate-600 dark:text-alt-dark-muted">Fréquence</label>
    <AltSelect
      :id="`${uid}-frequence`"
      :model-value="modelValue.frequence"
      :options="FREQUENCES.map((f) => ({ value: f, label: FREQUENCE_LABELS[f] }))"
      @update:model-value="(v: string) => maj({ frequence: v as Frequence })"
    />

    <div v-if="modelValue.frequence === 'hebdomadaire'" class="flex flex-wrap gap-3" role="group" aria-label="Jours de la semaine">
      <AltCheckbox
        v-for="j in JOURS_SEMAINE"
        :key="j"
        :model-value="(modelValue.joursSemaine ?? []).includes(j)"
        :label="JOUR_LABELS[j]"
        @update:model-value="(v: boolean) => basculerJour(j, v)"
      />
    </div>

    <div v-if="modelValue.frequence === 'mensuelle' || modelValue.frequence === 'annuelle'" class="flex flex-wrap gap-3">
      <div>
        <label :for="`${uid}-jour`" class="block text-xs text-slate-600 dark:text-alt-dark-muted">Jour du mois</label>
        <AltInput
          :id="`${uid}-jour`"
          type="number"
          min="1"
          max="31"
          class="w-24"
          :model-value="String(modelValue.jourMois ?? 1)"
          @update:model-value="(v: string) => maj({ jourMois: Number(v) })"
        />
      </div>
      <div v-if="modelValue.frequence === 'annuelle'">
        <label :for="`${uid}-mois`" class="block text-xs text-slate-600 dark:text-alt-dark-muted">Mois</label>
        <AltSelect
          :id="`${uid}-mois`"
          :model-value="String(modelValue.mois ?? 1)"
          :options="MOIS.map((m, i) => ({ value: String(i + 1), label: m }))"
          @update:model-value="(v: string) => maj({ mois: Number(v) })"
        />
      </div>
    </div>

    <p class="text-xs text-slate-600 dark:text-alt-dark-muted">
      {{ refus ?? `${decrireRegle(modelValue)} — prochaine demande le ${prochaine}.` }}
    </p>
  </fieldset>
</template>

<script setup lang="ts">
import { computed, useId } from 'vue';
import {
  FREQUENCES,
  FREQUENCE_LABELS,
  JOURS_SEMAINE,
  JOUR_LABELS,
  decrireRegle,
  prochaineEcheance,
  refusRegle,
  type Frequence,
  type Regle,
} from '@its/shared';
import { dateCourte } from '@/lib/affichage';

const props = defineProps<{ modelValue: Regle }>();
const emit = defineEmits<{ 'update:modelValue': [Regle] }>();
const uid = useId();

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const maj = (p: Partial<Regle>): void => emit('update:modelValue', { ...props.modelValue, ...p });

function basculerJour(j: number, coche: boolean): void {
  const jours = new Set(props.modelValue.joursSemaine ?? []);
  if (coche) jours.add(j);
  else jours.delete(j);
  maj({ joursSemaine: [...jours].sort() });
}

// Mêmes fonctions que l'API : l'écran annonce exactement ce que fera l'ordonnanceur.
const refus = computed(() => refusRegle(props.modelValue));
const prochaine = computed(() =>
  refus.value ? '' : dateCourte(prochaineEcheance(props.modelValue, new Date().toISOString().slice(0, 10))),
);
</script>
